import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Target } from "lucide-react";
import { api, errMsg, fmtDate, money } from "@/lib/api";
import { PageHeader, Progress, FormDialog, EmptyState } from "@/components/shared";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";

const METRICS = [
  { value: "revenue", label: "Revenue collected (auto)" }, { value: "projects_delivered", label: "Projects delivered (auto)" },
  { value: "tasks_completed", label: "Tasks completed (auto)" }, { value: "new_clients", label: "New clients (auto)" }, { value: "custom", label: "Custom (manual)" },
];

export default function Goals() {
  const { user } = useAuth();
  const isAdmin = user.role === "admin";
  const [goals, setGoals] = useState(null);
  const [team, setTeam] = useState([]);
  const [edit, setEdit] = useState(null);
  const load = () => api.get("/goals").then((r) => setGoals(r.data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { load(); api.get("/team").then((r) => setTeam(r.data)); }, []);

  const fields = [
    { name: "title", label: "Goal", required: true, full: true },
    { name: "metric", label: "Measured by", type: "select", options: METRICS },
    { name: "owner_id", label: "Owner", type: "select", options: team.map((m) => ({ value: m.id, label: m.name })) },
    { name: "target", label: "Target", type: "number", required: true }, { name: "current", label: "Current (manual goals only)", type: "number" },
    { name: "unit", label: "Unit", placeholder: "USD, projects, NPS…" },
    { name: "period_start", label: "Period start", type: "date" }, { name: "period_end", label: "Period end", type: "date" },
    { name: "description", label: "Why it matters", type: "textarea", full: true },
  ];
  const save = async (f) => {
    const body = { ...f, target: Number(f.target) || 0, current: Number(f.current) || 0, period_start: f.period_start || null, period_end: f.period_end || null, owner_id: f.owner_id || null };
    if (edit?.id) await api.put(`/goals/${edit.id}`, body); else await api.post("/goals", body);
    toast.success("Goal saved"); load();
  };
  const remove = async (g) => { if (!window.confirm(`Delete goal "${g.title}"?`)) return; await api.delete(`/goals/${g.id}`); load(); };
  if (!goals) return <div className="text-sm text-slate-500">Loading goals…</div>;
  const fmt = (g, v) => (g.metric === "revenue" || g.unit === "USD" ? money(v) : `${Math.round(v * 10) / 10}${g.unit ? ` ${g.unit}` : ""}`);

  return (
    <div data-testid="goals-page">
      <PageHeader eyebrow="Strategy" title="Goals" subtitle="Auto-tracked goals update themselves from revenue, projects, tasks and clients. Manual goals you update as you go."
        actions={isAdmin && <Button className="bg-slate-900 hover:bg-slate-800" onClick={() => setEdit({ metric: "revenue", owner_id: user.id })} data-testid="add-goal-btn"><Plus size={16} className="mr-1" />New goal</Button>} />
      {!goals.length ? <EmptyState title="No goals yet" hint="Set a revenue, delivery or growth target to keep the team aligned." testId="goals-empty" /> : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {goals.map((g, i) => {
            const days = g.period_end ? Math.ceil((new Date(g.period_end) - Date.now()) / 86400000) : null;
            return (
              <div key={g.id} className="panel panel-hover p-6 fade-up" style={{ animationDelay: `${i * 40}ms` }} data-testid={`goal-card-${g.id}`}>
                <div className="flex items-start gap-4">
                  <div className={`h-11 w-11 rounded-md grid place-items-center shrink-0 ${g.progress >= 100 ? "bg-emerald-50 text-emerald-600" : "bg-slate-900 text-white"}`}><Target size={18} /></div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <div><div className="font-heading font-semibold text-slate-900">{g.title}</div><div className="text-xs text-slate-500 mt-0.5">{METRICS.find((m) => m.value === g.metric)?.label} · {g.owner_name || "Unowned"}</div></div>
                      {(isAdmin || g.metric === "custom") && <div className="flex gap-1"><button onClick={() => setEdit(g)} className="text-slate-400 hover:text-slate-900" data-testid={`edit-goal-${g.id}`}><Pencil size={14} /></button>{isAdmin && <button onClick={() => remove(g)} className="text-slate-400 hover:text-rose-600" data-testid={`delete-goal-${g.id}`}><Trash2 size={14} /></button>}</div>}
                    </div>
                    {g.description && <p className="text-sm text-slate-600 mt-2">{g.description}</p>}
                    <div className="flex items-end justify-between mt-5"><div className="num text-3xl font-semibold text-slate-900" data-testid={`goal-current-${g.id}`}>{fmt(g, g.current)}</div><div className="text-xs text-slate-500 num">of {fmt(g, g.target)} · <span className={g.progress >= 100 ? "text-emerald-600 font-semibold" : ""}>{g.progress}%</span></div></div>
                    <Progress value={g.progress} tone={g.progress >= 100 ? "bg-emerald-500" : g.progress >= 50 ? "bg-blue-600" : "bg-amber-500"} className="mt-2 h-2" />
                    <div className="text-[11px] text-slate-400 mt-2">{g.period_start ? `${fmtDate(g.period_start)} → ${fmtDate(g.period_end)}` : "No period"}{days !== null && ` · ${days >= 0 ? `${days} days left` : "period ended"}`}</div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
      <FormDialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)} title={edit?.id ? "Edit goal" : "New goal"} fields={fields} initial={edit || {}} onSubmit={save} testId="goal-dialog" />
    </div>
  );
}
